import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, CircleCheck, LifeBuoy, Send, type LucideIcon } from 'lucide-react';
import {
  SUPPORT,
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_LABELS,
  type SupportCategory,
  type SupportMessage,
  type SupportTicket,
  type SupportTicketDetail,
  type SupportTicketStatus,
} from '@spawnwire/shared';
import { Button, clsx, Field, Input, Select, TextArea } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { formatTimestamp } from '../../lib/time';

export type StatusLabels = Record<SupportTicketStatus, { label: string; dot: string }>;

/** How a ticket's status reads to the person who opened it. */
export const STATUS_INFO: StatusLabels = {
  open: { label: 'Waiting for support', dot: 'bg-warning' },
  answered: { label: 'Support replied', dot: 'bg-accent' },
  closed: { label: 'Closed', dot: 'bg-faint' },
};

export function StatusPill({ status, labels = STATUS_INFO }: { status: SupportTicketStatus; labels?: StatusLabels }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-main px-2.5 py-0.5 text-xs font-medium text-muted">
      <span className={clsx('size-2 rounded-full', labels[status].dot)} aria-hidden />
      {labels[status].label}
    </span>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate(to)} className="flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg">
      <ArrowLeft className="size-4" aria-hidden /> {children}
    </button>
  );
}

/** "Game server · My SMP" for a ticket's details line. */
export function ticketTopic(ticket: SupportTicket): string {
  return `${SUPPORT_CATEGORY_LABELS[ticket.category]}${ticket.server ? ` · ${ticket.server.name}` : ''}`;
}

export function TicketHeader({ ticket, labels, children }: { ticket: SupportTicket; labels?: StatusLabels; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-faint">Ticket #{ticket.number}</p>
        <h2 className="mt-1 break-words text-xl font-bold text-fg">{ticket.subject}</h2>
        <p className="mt-1 text-sm text-muted">
          {ticketTopic(ticket)} · Opened {formatTimestamp(ticket.createdAt)}
        </p>
        {children}
      </div>
      <StatusPill status={ticket.status} labels={labels} />
    </div>
  );
}

/** The messages on a ticket, oldest first. `who` names each message's sender. */
export function Conversation<M extends SupportMessage>({ messages, who }: { messages: M[]; who: (m: M) => ReactNode }) {
  return (
    <ol className="space-y-3" aria-label="Messages">
      {messages.map((m) => (
        <li key={m.id} className={clsx('rounded-xl border p-4', m.fromStaff ? 'border-accent/30 bg-accent-soft' : 'border-line bg-sidebar')}>
          <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {m.fromStaff && <LifeBuoy className="size-4 text-accent" aria-hidden />}
            <span className="font-semibold text-fg">{who(m)}</span>
            <span className="text-xs text-faint">{formatTimestamp(m.createdAt)}</span>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-fg">{m.body}</p>
        </li>
      ))}
    </ol>
  );
}

export interface ReplyAction {
  label: string;
  icon?: LucideIcon;
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Sends what was typed (and needs something typed); otherwise the text box is left as it is. */
  sends: boolean;
  run: (text: string) => Promise<void>;
}

/** A reply box. The first action is the main one (Ctrl+Enter); actions read right to left. */
export function ReplyBox({ label, placeholder, note, actions }: { label: string; placeholder: string; note?: ReactNode; actions: ReplyAction[] }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasText = text.trim().length > 0;

  async function go(action: ReplyAction) {
    if (busy || (action.sends && !hasText)) return;
    setBusy(action.label);
    setError(null);
    try {
      await action.run(text.trim());
      if (action.sends) setText('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void go(actions[0]!);
      }}
      className="rounded-xl border border-line bg-sidebar p-4"
    >
      <label htmlFor="support-reply" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">
        {label}
      </label>
      <TextArea
        id="support-reply"
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void go(actions[0]!);
          }
        }}
        maxLength={SUPPORT.messageMax}
        placeholder={placeholder}
      />
      {note && <p className="mt-2 text-xs text-faint">{note}</p>}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-row-reverse flex-wrap justify-start gap-2">
        {actions.map((a, i) => (
          <Button
            key={a.label}
            type={i === 0 ? 'submit' : 'button'}
            size="sm"
            variant={a.variant ?? (i === 0 ? 'primary' : 'secondary')}
            icon={a.icon}
            loading={busy === a.label}
            disabled={(!!busy && busy !== a.label) || (a.sends && !hasText)}
            onClick={i === 0 ? undefined : () => void go(a)}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </form>
  );
}

/** The person's side of their ticket: reply, or say it's solved. */
export function PersonReply({ ticket, send }: { ticket: SupportTicketDetail; send: (action: 'messages' | 'close', body?: { message: string }) => Promise<void> }) {
  const closed = ticket.status === 'closed';
  return (
    <ReplyBox
      label={closed ? 'Still need help?' : 'Reply'}
      placeholder={closed ? 'Tell us what is still wrong.' : 'Add details or answer our questions.'}
      note={closed ? 'This ticket is closed. Replying opens it again.' : 'Ctrl+Enter sends. Spawnwire staff will never ask for your password.'}
      actions={[
        { label: 'Send reply', icon: Send, sends: true, run: (message) => send('messages', { message }) },
        ...(closed ? [] : [{ label: 'Mark as solved', icon: CircleCheck, variant: 'ghost' as const, sends: false, run: () => send('close') }]),
      ]}
    />
  );
}

export interface TicketFormValues {
  category: SupportCategory;
  subject: string;
  message: string;
  email?: string;
  serverId?: string | null;
}

const PLACEHOLDER: Record<SupportCategory, string> = {
  game_server: 'What happened, and what did you expect? Include any error from the console.',
  account: "What happens when you try to sign in? Don't include your password.",
  bug: 'What were you doing, what went wrong, and does it happen every time?',
  safety: 'Who or what is it about, and what happened? Links to messages help.',
  other: 'How can we help?',
};

/**
 * A new ticket. `askEmail` is for people who are signed out; `servers` lets signed-in people say which of their
 * game servers it is about.
 */
export function TicketForm({
  askEmail,
  servers = [],
  defaultCategory,
  onSubmit,
}: {
  askEmail: boolean;
  servers?: { id: string; name: string }[];
  defaultCategory: SupportCategory;
  onSubmit: (values: TicketFormValues) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState<SupportCategory>(defaultCategory);
  const [serverId, setServerId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pickServer = category === 'game_server' && servers.length > 0;
  const ready = subject.trim().length >= 3 && message.trim().length >= 10 && (!askEmail || email.trim().length > 3);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        category,
        subject: subject.trim(),
        message: message.trim(),
        ...(askEmail ? { email: email.trim() } : {}),
        ...(pickServer && serverId ? { serverId } : {}),
      });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {askEmail && (
        <Field label="Email" htmlFor="support-email" hint="The email address your Spawnwire account is registered with, so we can find it.">
          <Input id="support-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} required />
        </Field>
      )}
      <Field label="What do you need help with?" htmlFor="support-category">
        <Select id="support-category" value={category} onChange={setCategory} options={SUPPORT_CATEGORIES.map((c) => ({ value: c, label: SUPPORT_CATEGORY_LABELS[c] }))} />
      </Field>
      {pickServer && (
        <Field label="Which server?" htmlFor="support-server">
          <Select id="support-server" value={serverId} onChange={setServerId} options={[{ value: '', label: 'Not about one server' }, ...servers.map((s) => ({ value: s.id, label: s.name }))]} />
        </Field>
      )}
      <Field label="Subject" htmlFor="support-subject">
        <Input id="support-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={SUPPORT.subjectMax} placeholder="A few words about the problem" required />
      </Field>
      <Field label="Message" htmlFor="support-message" hint="Never send your password. Spawnwire staff will never ask for it.">
        <TextArea id="support-message" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={SUPPORT.messageMax} placeholder={PLACEHOLDER[category]} required />
      </Field>
      {error && (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" icon={Send} loading={busy} disabled={!ready}>
          Send to support
        </Button>
      </div>
    </form>
  );
}
